import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { blink } from '@/lib/blink';
import { RefreshCw, CheckCircle2, XCircle, Clock, TrendingUp, Mail, Users } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface SyncLog {
  id: string;
  runTimestamp: string;
  status: 'started' | 'success' | 'error';
  usersProcessed: number;
  totalSynced: number;
  newEmails: number;
  updatedEmails: number;
  errorsCount: number;
  durationMs: number;
  errorMessage?: string;
  details?: string;
  createdAt: string;
}

export default function EmailSyncMonitor() {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggeringSync, setTriggeringSync] = useState(false);
  const [stats, setStats] = useState({
    totalRuns: 0,
    successRate: 0,
    avgDuration: 0,
    totalEmailsToday: 0,
    lastRunTime: '',
    timeSinceLastRun: 0
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const allLogs = await blink.db.emailSyncLogs.list({
        orderBy: { runTimestamp: 'desc' },
        limit: 50
      });

      setLogs(allLogs as SyncLog[]);

      // Calculate stats
      if (allLogs.length > 0) {
        const lastLog = allLogs[0] as SyncLog;
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const last24Hours = allLogs.filter(l => l.runTimestamp >= oneDayAgo) as SyncLog[];
        
        const successCount = last24Hours.filter(l => l.status === 'success').length;
        const successRate = last24Hours.length > 0 
          ? (successCount / last24Hours.length * 100) 
          : 0;
        
        const avgDuration = last24Hours.length > 0
          ? last24Hours.reduce((sum, l) => sum + (l.durationMs || 0), 0) / last24Hours.length
          : 0;
        
        const totalEmailsToday = last24Hours.reduce((sum, l) => sum + (l.totalSynced || 0), 0);
        const timeSinceLastRun = Date.now() - new Date(lastLog.runTimestamp).getTime();

        setStats({
          totalRuns: allLogs.length,
          successRate,
          avgDuration,
          totalEmailsToday,
          lastRunTime: lastLog.runTimestamp,
          timeSinceLastRun
        });
      }
    } catch (error) {
      console.error('Failed to fetch sync logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const triggerManualSync = async () => {
    setTriggeringSync(true);
    try {
      const response = await fetch('https://ben3ou87--scheduled-email-sync.functions.blink.new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });

      const result = await response.json();
      console.log('Manual sync result:', result);
      
      // Refresh logs after sync
      setTimeout(fetchLogs, 2000);
    } catch (error) {
      console.error('Failed to trigger sync:', error);
    } finally {
      setTriggeringSync(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Success</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      case 'started':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Running</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Email Sync Monitor</h1>
            <p className="text-muted-foreground">Real-time monitoring of scheduled email syncs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isStale = stats.timeSinceLastRun > 10 * 60 * 1000; // Over 10 minutes

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Email Sync Monitor</h1>
          <p className="text-muted-foreground">Real-time monitoring of scheduled email syncs</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchLogs}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={triggerManualSync}
            disabled={triggeringSync}
          >
            <Clock className={`w-4 h-4 mr-2 ${triggeringSync ? 'animate-spin' : ''}`} />
            {triggeringSync ? 'Syncing...' : 'Trigger Sync'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              Last Sync
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.lastRunTime ? formatDistanceToNow(new Date(stats.lastRunTime), { addSuffix: true }) : 'Never'}
            </div>
            {isStale && (
              <p className="text-sm text-red-500 mt-1">⚠️ Overdue - check cron</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-1" />
              Success Rate (24h)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.successRate.toFixed(1)}%
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {logs.filter(l => l.status === 'success').length} / {stats.totalRuns} runs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center">
              <Mail className="w-4 h-4 mr-1" />
              Emails Today
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {stats.totalEmailsToday.toLocaleString()}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Synced in last 24 hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              Avg Duration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(stats.avgDuration / 1000).toFixed(1)}s
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Per sync run
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sync Runs</CardTitle>
          <CardDescription>Last 50 scheduled email sync executions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No sync logs found. The cron job may not be running yet.
              </div>
            ) : (
              logs.map((log) => (
                <div 
                  key={log.id} 
                  className="flex items-start justify-between border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(log.status)}
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.runTimestamp), 'MMM dd, yyyy HH:mm:ss')}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({formatDistanceToNow(new Date(log.runTimestamp), { addSuffix: true })})
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Users:</span>
                        <span className="ml-2 font-medium">{log.usersProcessed || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Synced:</span>
                        <span className="ml-2 font-medium">{log.totalSynced || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">New:</span>
                        <span className="ml-2 font-medium text-green-600">{log.newEmails || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Updated:</span>
                        <span className="ml-2 font-medium text-blue-600">{log.updatedEmails || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="ml-2 font-medium">{((log.durationMs || 0) / 1000).toFixed(1)}s</span>
                      </div>
                    </div>

                    {log.status === 'error' && log.errorMessage && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-950 rounded text-sm text-red-600 dark:text-red-400">
                        <strong>Error:</strong> {log.errorMessage}
                      </div>
                    )}

                    {log.errorsCount > 0 && log.status !== 'error' && (
                      <div className="mt-2 text-sm text-orange-600">
                        ⚠️ {log.errorsCount} user(s) failed to sync
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {logs.length === 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Cron Job Setup Required
            </CardTitle>
            <CardDescription>
              Set up a cron job to automatically sync emails every 5 minutes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Quick Setup with Cron-Job.org:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Visit <a href="https://cron-job.org" target="_blank" rel="noopener noreferrer" className="text-primary underline">cron-job.org</a></li>
                <li>Create a free account</li>
                <li>Add new cron job with URL: <code className="bg-muted px-2 py-1 rounded text-xs">https://ben3ou87--scheduled-email-sync.functions.blink.new</code></li>
                <li>Set schedule to: <code className="bg-muted px-2 py-1 rounded text-xs">*/5 * * * *</code> (every 5 minutes)</li>
                <li>Method: POST, Body: <code className="bg-muted px-2 py-1 rounded text-xs">{'{}'}</code></li>
              </ol>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Test Manual Sync:</h4>
              <Button onClick={triggerManualSync} disabled={triggeringSync}>
                {triggeringSync ? 'Syncing...' : 'Test Sync Now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
